import type { Cell, Worksheet } from "exceljs";
import type { ReportCell, ReportItem, ReportTable, SimulationReport } from "./report";
import { NOT_PROVIDED } from "./report";

export const MONEY_FORMAT = '#,##0.00 "€"';
export const PERCENT_FORMAT = "0.00 %";
const HEADER_FILL = "FFE9E1E2";
const TITLE_COLOR = "FF664F4E";
const MUTED_COLOR = "FF656565";

function write(cell: Cell, value: ReportCell) {
	if (value.type === "text") {
		cell.value = value.value;
		return;
	}
	cell.value = value.value ?? NOT_PROVIDED;
	if (value.value === null) return;
	cell.numFmt = value.type === "money" ? MONEY_FORMAT : value.type === "percent" ? PERCENT_FORMAT : "0";
}

function section(sheet: Worksheet, label: string) {
	if (sheet.rowCount > 0) sheet.addRow([]);
	const row = sheet.addRow([label]);
	row.font = { bold: true, size: 12, color: { argb: TITLE_COLOR } };
}

function items(sheet: Worksheet, list: ReportItem[]) {
	for (const item of list) {
		const row = sheet.addRow([item.label]);
		write(row.getCell(2), item.value);
		if (item.note) {
			row.getCell(3).value = item.note;
			row.getCell(3).font = { color: { argb: MUTED_COLOR } };
		}
	}
}

function table(sheet: Worksheet, { columns, rows }: ReportTable) {
	const header = sheet.addRow(columns);
	header.eachCell((cell) => {
		cell.font = { bold: true };
		cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEADER_FILL } };
		cell.alignment = { vertical: "middle", wrapText: true };
	});
	if (!rows.length) sheet.addRow(["Non renseignée"]);
	for (const values of rows) {
		const row = sheet.addRow([]);
		values.forEach((value, index) => write(row.getCell(index + 1), value));
	}
}

/** Long texts span the three columns of a sheet. */
function paragraphs(sheet: Worksheet, texts: string[]) {
	for (const content of texts) {
		const row = sheet.addRow([content]);
		sheet.mergeCells(row.number, 1, row.number, 3);
		row.getCell(1).alignment = { wrapText: true, vertical: "top" };
		row.height = Math.max(15, Math.ceil(content.length / 95) * 15);
	}
}

/** Classeur de la simulation affichée : synthèse, tableau annuel, allocation et frais, répartition. */
export async function buildExcelWorkbook(report: SimulationReport): Promise<ArrayBuffer> {
	const { Workbook } = await import("exceljs");
	const workbook = new Workbook();
	workbook.creator = "Wenimmo";
	workbook.title = report.title;

	const summary = workbook.addWorksheet("Synthèse");
	summary.columns = [{ width: 44 }, { width: 22 }, { width: 46 }];
	summary.addRow([report.title]).font = { bold: true, size: 16, color: { argb: TITLE_COLOR } };
	summary.addRow([report.summary]);
	summary.addRow([report.generatedLabel]).font = { color: { argb: MUTED_COLOR } };
	section(summary, "Hypothèses");
	items(summary, report.hypotheses);
	section(summary, "Résultats clés");
	items(summary, report.keyFigures);
	if (report.warnings.length) {
		section(summary, "Avertissements");
		paragraphs(summary, report.warnings);
	}
	section(summary, "À savoir");
	paragraphs(summary, report.notes);

	const annual = workbook.addWorksheet("Tableau annuel", { views: [{ state: "frozen", ySplit: 1 }] });
	annual.columns = report.annualDetail.columns.map((_, index) => ({ width: index === 0 ? 10 : 18 }));
	table(annual, report.annualDetail);

	const allocation = workbook.addWorksheet("Allocation et frais");
	allocation.columns = [{ width: 34 }, { width: 32 }, { width: 18 }, { width: 22 }, { width: 16 }, { width: 14 }, { width: 18 }];
	section(allocation, "Allocation retenue");
	table(allocation, report.allocation);
	section(allocation, "Frais par SCPI");
	table(allocation, report.fees);
	section(allocation, "Total des frais de la simulation");
	items(allocation, report.feeTotals);
	section(allocation, "Sources des données");
	table(allocation, report.sources);

	const exposure = workbook.addWorksheet("Répartition");
	exposure.columns = [{ width: 40 }, { width: 14 }, { width: 40 }];
	section(exposure, "Répartition géographique");
	table(exposure, report.geography);
	section(exposure, "Répartition sectorielle");
	table(exposure, report.sectors);

	return (await workbook.xlsx.writeBuffer()) as ArrayBuffer;
}
